// PRD 3.1: 对话流组件 —— 串联 SSE 解析与 Markdown 渲染
// PRD 4.2: 流式渲染保持 60fps，使用 requestAnimationFrame 节流增量 DOM 更新
import { renderMarkdown, enhanceMessageEl } from '../core/markdown.js';

const ROLE_LABELS = {
  user: '来访专家',
  assistant: 'Mythra 助手',
  system: 'sys',
};

export class ChatStream {
  constructor(container) {
    this.container = container;
    /** @type {(text: string) => void} */
    this.onFollowUpSelect = () => {};
  }

  /** @param {(text: string) => void} cb */
  onFollowUpSelectCb(cb) {
    this.onFollowUpSelect = cb;
  }

  scrollToBottom() {
    this.container.scrollTop = this.container.scrollHeight;
  }

  /**
   * 追加一条完整消息
   * @param {'user'|'assistant'|'system'} role
   * @param {string} content
   */
  appendMessage(role, content = '') {
    const el = document.createElement('div');
    el.className = `msg msg--${role}`;
    el.innerHTML = `<span class="msg__role">${ROLE_LABELS[role] ?? role}</span><div class="msg__content"></div>`;
    const contentEl = el.querySelector('.msg__content');
    if (content) {
      contentEl.innerHTML = renderMarkdown(content);
      if (role === 'assistant') enhanceMessageEl(contentEl);
    }
    this.container.appendChild(el);
    this.scrollToBottom();
    return el;
  }

  /**
   * 创建一条流式消息（用于 LLM 响应）
   * 返回 controller：thinking / append / done / error / appendFollowUp
   */
  createStreamingMessage(role = 'assistant') {
    const el = this.appendMessage(role);
    const contentEl = el.querySelector('.msg__content');
    el.classList.add('cursor-blink');

    // follow-up 容器独立于 contentEl，避免被流式 innerHTML 重渲染冲掉
    const followupEl = document.createElement('div');
    followupEl.className = 'msg__followup';
    el.appendChild(followupEl);

    const self = this;

    let buffer = '';
    let rafScheduled = false;
    let thinkingActive = false;

    const scrollToBottom = () => this.scrollToBottom();

    const render = () => {
      contentEl.innerHTML = renderMarkdown(buffer);
      scrollToBottom();
    };

    // PRD 4.2: 用 rAF 节流，每帧最多渲染一次，避免主线程阻塞
    const scheduleRender = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        rafScheduled = false;
        render();
      });
    };

    return {
      /** 显示"思考中..."占位，在首次 append 前调用 */
      thinking() {
        if (thinkingActive) return;
        thinkingActive = true;
        contentEl.innerHTML =
          '<span class="msg__thinking">思考中<span class="msg__thinking__dot">.</span><span class="msg__thinking__dot">.</span><span class="msg__thinking__dot">.</span></span>';
        scrollToBottom();
      },
      /** @param {string} text */
      append(text) {
        if (thinkingActive) {
          thinkingActive = false;
          buffer = '';
        }
        buffer += text;
        scheduleRender();
      },
      done() {
        const finalize = () => {
          render();
          enhanceMessageEl(contentEl); // 流式结束后统一加高亮 + 复制按钮
          el.classList.remove('cursor-blink');
          scrollToBottom();
          // DEBUG: 验证橙色加粗渲染
          console.log('[done] buffer:', JSON.stringify(buffer.slice(-200)));
          console.log('[done] strong count:', contentEl.querySelectorAll('strong').length);
          console.log('[done] html tail:', contentEl.innerHTML.slice(-300));
        };
        if (rafScheduled) {
          // 等待已调度的帧完成后再做最终增强
          requestAnimationFrame(finalize);
        } else {
          finalize();
        }
      },
      /** @param {string} msg */
      error(msg) {
        thinkingActive = false;
        el.classList.remove('cursor-blink');
        el.classList.remove('msg--assistant');
        el.classList.add('msg--system');
        contentEl.textContent = `出错：${msg}`;
        scrollToBottom();
      },
      /** @param {string[]} items */
      appendFollowUp(items) {
        followupEl.replaceChildren();
        items.forEach((text) => {
          const item = document.createElement('div');
          item.className = 'msg__followup-item';
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.textContent = text;
          item.addEventListener('click', () => self.onFollowUpSelect(text));
          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              self.onFollowUpSelect(text);
            }
          });
          followupEl.appendChild(item);
        });
        scrollToBottom();
      },
    };
  }
}
